import { ref, computed } from 'vue';
import type { AxiosError } from 'axios';
import { useNotification } from '@asabdev/asab-ui';
import {
  AUTH_ERROR_CODES,
  getErrorMessage,
  isAuthError,
  isNetworkError,
  isValidationError,
} from '@/lib/api/errors';
import { withRetry, type RetryOptions } from '@/lib/api/retry';

// Auth error codes handled by per-page banners. handleError MUST NOT show
// a global toast for these — the page renders an inline message tailored
// to the status (waitlist link for pending, billing link for suspended,
// etc.). Showing a transient toast on top would be redundant + jarring.
const PAGE_HANDLED_AUTH_CODES = [
  AUTH_ERROR_CODES.LOGIN_PENDING_APPROVAL,
  AUTH_ERROR_CODES.LOGIN_ACCOUNT_BANNED,
  AUTH_ERROR_CODES.LOGIN_ACCOUNT_SUSPENDED,
  AUTH_ERROR_CODES.LOGIN_ACCOUNT_DEACTIVATED,
  AUTH_ERROR_CODES.LOGIN_ACCOUNT_DISABLED,
  AUTH_ERROR_CODES.LOGIN_USER_NOT_VERIFIED,
  AUTH_ERROR_CODES.REGISTER_USER_ALREADY_EXISTS,
  AUTH_ERROR_CODES.REGISTER_PENDING_APPROVAL,
];

function isPageHandledAuthError(error: AxiosError): boolean {
  return PAGE_HANDLED_AUTH_CODES.some(code => isAuthError(error, code));
}

/**
 * Composable for handling API errors with retry logic and user feedback
 */
export function useApiError() {
  const { showError, showWarning, showInfo } = useNotification();

  // Error state
  const lastError = ref<AxiosError | null>(null);
  const isRetrying = ref(false);
  const retryCount = ref(0);

  // Computed properties
  const hasError = computed(() => lastError.value !== null);
  const isNetworkIssue = computed(() => lastError.value && isNetworkError(lastError.value));
  const isValidationIssue = computed(() => lastError.value && isValidationError(lastError.value));
  const isAuthIssue = computed(() => lastError.value && lastError.value.response?.status === 401);

  /**
   * Handle API errors with appropriate user feedback
   */
  const handleError = (error: AxiosError, context?: string) => {
    lastError.value = error;

    // Don't show notifications for validation errors - let forms handle them
    if (isValidationError(error)) {
      return;
    }

    // Skip global toast for auth errors that pages render inline (pending
    // approval banner, banned/suspended banners, verify-email prompt, etc.).
    // The error still propagates so the form's catch can emit the right event.
    if (isPageHandledAuthError(error)) {
      return;
    }

    // Extract user-friendly message
    const message = getErrorMessage(error);
    const title = getErrorTitle(error, context);

    // Show appropriate notification based on error type
    if (isNetworkError(error)) {
      showWarning(message, title);
    } else if (error.response?.status === 401) {
      showError(message, title);
    } else if (error.response?.status === 403) {
      showWarning(message, title);
    } else if (error.response?.status === 429) {
      showInfo(message, title);
    } else if (error.response && error.response.status >= 500) {
      showError(message, title);
    } else {
      showError(message, title);
    }
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    lastError.value = null;
    retryCount.value = 0;
    isRetrying.value = false;
  };

  /**
   * Wrapper for API calls with automatic error handling
   */
  const safeApiCall = async <T>(
    apiCall: () => Promise<T>,
    options: {
      context?: string;
      showSuccessMessage?: boolean;
      successMessage?: string;
      retryOptions?: RetryOptions;
    } = {}
  ): Promise<T | null> => {
    const { context, showSuccessMessage = false, successMessage, retryOptions } = options;

    try {
      clearError();

      let result: T;
      if (retryOptions) {
        result = await withRetry(apiCall, {
          ...retryOptions,
          showNotifications: false, // We'll handle notifications ourselves
          onRetry: (error: AxiosError, count: number) => {
            retryCount.value = count;
            isRetrying.value = true;

            // Show retry notification
            const delay = retryOptions.initialDelay || 1000;
            const timeRemaining = (delay / 1000).toFixed(1);
            let retryMessage = `Retrying request in ${timeRemaining}s... (${count}/${retryOptions.maxRetries || 3})`;

            if (isNetworkError(error)) {
              retryMessage = `Connection failed. Retrying in ${timeRemaining}s... (${count}/${retryOptions.maxRetries || 3})`;
              showWarning(retryMessage, 'Retrying Connection');
            } else if (error.response?.status === 429) {
              retryMessage = `Rate limit exceeded. Waiting ${timeRemaining}s before retry... (${count}/${retryOptions.maxRetries || 3})`;
              showInfo(retryMessage, 'Rate Limited');
            } else if (error.response && error.response.status >= 500) {
              retryMessage = `Server error. Retrying in ${timeRemaining}s... (${count}/${retryOptions.maxRetries || 3})`;
              showWarning(retryMessage, 'Server Issue');
            } else {
              showWarning(retryMessage, 'Retrying Request');
            }

            retryOptions.onRetry?.(error, count);
          },
        });
      } else {
        result = await apiCall();
      }

      isRetrying.value = false;

      if (showSuccessMessage) {
        const message = successMessage || 'Operation completed successfully';
        showInfo(message);
      }

      return result;
    } catch (error) {
      isRetrying.value = false;
      handleError(error as AxiosError, context);
      // Page-handled auth errors must propagate so the form's catch can
      // emit the right event (account-disabled banner, verification prompt
      // redirect, etc.). Other errors are swallowed and signalled by null.
      if (isPageHandledAuthError(error as AxiosError)) {
        throw error;
      }
      return null;
    }
  };

  /**
   * Wrapper for authentication API calls
   */
  const safeAuthCall = async <T>(
    apiCall: () => Promise<T>,
    context?: string
  ): Promise<T | null> => {
    return safeApiCall(apiCall, {
      context: context || 'Authentication',
      retryOptions: {
        maxRetries: 3,
        initialDelay: 2000,
        retryCondition: (error: AxiosError) => {
          // Retry on network errors and server errors for auth
          return (
            isNetworkError(error) || (error.response?.status ? error.response.status >= 500 : false)
          );
        },
      },
    });
  };

  /**
   * Wrapper for file operation API calls
   */
  const safeFileCall = async <T>(
    apiCall: () => Promise<T>,
    context?: string
  ): Promise<T | null> => {
    return safeApiCall(apiCall, {
      context: context || 'File Operation',
      retryOptions: {
        maxRetries: 5,
        initialDelay: 2000,
        retryCondition: (error: AxiosError) => {
          return (
            isNetworkError(error) ||
            (error.response?.status ? error.response.status >= 500 : false) ||
            error.response?.status === 429
          );
        },
      },
    });
  };

  /**
   * Wrapper for critical operations that need aggressive retrying
   */
  const safeCriticalCall = async <T>(
    apiCall: () => Promise<T>,
    context?: string
  ): Promise<T | null> => {
    return safeApiCall(apiCall, {
      context: context || 'Critical Operation',
      retryOptions: {
        maxRetries: 5,
        retryCondition: (error: AxiosError) => {
          return (
            isNetworkError(error) ||
            (error.response?.status ? error.response.status >= 500 : false) ||
            error.response?.status === 429 ||
            error.response?.status === 408
          );
        },
      },
    });
  };

  /**
   * Check if an error should trigger a retry
   */
  const shouldRetry = (error: AxiosError): boolean => {
    return (
      isNetworkError(error) ||
      (error.response?.status ? error.response.status >= 500 : false) ||
      error.response?.status === 429
    );
  };

  /**
   * Get validation errors for form handling
   */
  const getValidationErrors = (error: AxiosError): Record<string, string> => {
    if (!isValidationError(error)) {
      return {};
    }

    const responseData = error.response?.data as unknown;
    const errors: Record<string, string> = {};

    if (responseData && typeof responseData === 'object' && 'detail' in responseData) {
      const detail = (responseData as { detail: unknown }).detail;
      if (Array.isArray(detail)) {
        // Handle FastAPI validation errors
        detail.forEach((err: unknown) => {
          if (err && typeof err === 'object' && 'loc' in err && 'msg' in err) {
            const errorObj = err as { loc: unknown[]; msg: string };
            if (Array.isArray(errorObj.loc) && errorObj.loc.length > 0) {
              const field = String(errorObj.loc[errorObj.loc.length - 1]);
              errors[field] = errorObj.msg;
            }
          }
        });
      }
    } else if (responseData && typeof responseData === 'object' && 'errors' in responseData) {
      const errorsObj = (responseData as { errors: unknown }).errors;
      // Handle other validation error formats
      if (errorsObj && typeof errorsObj === 'object') {
        Object.entries(errorsObj as Record<string, unknown>).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            errors[field] = messages[0];
          } else {
            errors[field] = messages as string;
          }
        });
      }
    }

    return errors;
  };

  return {
    // State
    lastError: computed(() => lastError.value),
    hasError,
    isNetworkIssue,
    isValidationIssue,
    isAuthIssue,
    isRetrying: computed(() => isRetrying.value),
    retryCount: computed(() => retryCount.value),

    // Methods
    handleError,
    clearError,
    safeApiCall,
    safeAuthCall,
    safeFileCall,
    safeCriticalCall,
    shouldRetry,
    getValidationErrors,
  };
}

/**
 * Get appropriate error title based on error type and context
 */
function getErrorTitle(error: AxiosError, context?: string): string {
  if (context) {
    return `${context} Error`;
  }

  if (isNetworkError(error)) {
    return 'Connection Error';
  }

  if (error.response?.status === 401) {
    return 'Authentication Error';
  }

  if (error.response?.status === 403) {
    return 'Permission Error';
  }

  if (error.response?.status === 429) {
    return 'Rate Limit';
  }

  if (error.response && error.response.status >= 500) {
    return 'Server Error';
  }

  if (error.response?.status === 422) {
    return 'Validation Error';
  }

  return 'Request Error';
}
